import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { graphqlApi } from '../../services/graphql';
import { useAuthStore } from '../../store/authStore';
import ChannelIcon from './ChannelIcon';

interface LoadCampaignModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (campaign: any) => void;
}

const LoadCampaignModal: React.FC<LoadCampaignModalProps> = ({ isOpen, onClose, onSelect }) => {
  const { t } = useTranslation(['campaigns', 'common']);
  const { user } = useAuthStore();
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const fetchCampaigns = useCallback(async () => {
    if (!user?.hostId) return;
    setLoading(true);
    try {
      const res = await graphqlApi.getCampaignsByHost(user.hostId);
      if (res.success && res.data) {
        setCampaigns(res.data.campaigns || []);
      }
    } catch (err) {
      console.error('Failed to fetch campaigns:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.hostId]);

  useEffect(() => {
    if (isOpen) {
      fetchCampaigns();
      setSelectedId(null);
      setSearch('');
    }
  }, [isOpen, fetchCampaigns]);

  const filtered = search
    ? campaigns.filter(c =>
        (c.campaignName || '').toLowerCase().includes(search.toLowerCase()) ||
        (c.brandName || '').toLowerCase().includes(search.toLowerCase())
      )
    : campaigns;

  const handleLoad = () => {
    const campaign = campaigns.find(c => c.campaignId === selectedId);
    if (campaign) {
      onSelect(campaign);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900">{t('campaigns:registration.loadCampaign')}</h2>
            <p className="text-sm text-gray-500 mt-0.5">{t('campaigns:registration.v2.loadCampaignDesc')}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Search */}
        <div className="px-6 py-3 border-b border-gray-100">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                placeholder={t('common:search')}
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:outline-none focus:ring-blue-500" />
            </div>
            <button type="button"
              className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">
              {t('common:search')}
            </button>
          </div>
        </div>

        {/* Campaign List */}
        <div className="flex-1 overflow-y-auto px-6 py-3">
          {loading ? (
            <div className="text-center py-8 text-gray-400">{t('common:loading')}</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-8 text-gray-400">{t('campaigns:registration.v2.noCampaignsFound')}</div>
          ) : (
            <div className="space-y-2">
              {filtered.map(campaign => {
                const isSelected = selectedId === campaign.campaignId;
                const thumbnail = campaign.representativeImages?.[0]?.fileUrl;
                return (
                  <div
                    key={campaign.campaignId}
                    onClick={() => setSelectedId(campaign.campaignId)}
                    className={`flex items-center gap-4 p-3 rounded-lg cursor-pointer transition-all ${
                      isSelected
                        ? 'bg-blue-50 border-2 border-blue-500 shadow-sm'
                        : 'bg-white border border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {/* Thumbnail */}
                    <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                      {thumbnail ? (
                        <img src={thumbnail} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-300 text-xs">No img</div>
                      )}
                    </div>

                    {/* Campaign Name + Channel */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className={`text-sm font-medium truncate ${isSelected ? 'text-blue-700' : 'text-gray-900'}`}>
                          {campaign.campaignName || t('campaigns:registration.v2.untitled')}
                        </p>
                        {campaign.contentChannel && (
                          <ChannelIcon channel={campaign.contentChannel} className="w-4 h-4 flex-shrink-0" />
                        )}
                      </div>
                      {campaign.brandName && (
                        <p className="text-xs text-gray-500 mt-0.5">{campaign.brandName}</p>
                      )}
                    </div>

                    {/* Load Button */}
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setSelectedId(campaign.campaignId); }}
                      className={`px-4 py-1.5 text-xs font-medium rounded-lg transition-colors flex-shrink-0 ${
                        isSelected
                          ? 'bg-blue-600 text-white'
                          : 'text-blue-600 border border-blue-300 hover:bg-blue-50'
                      }`}
                    >
                      {t('campaigns:registration.v2.loadBtn')}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button type="button" onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
            {t('common:cancel')}
          </button>
          <button type="button" onClick={handleLoad} disabled={!selectedId}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
            {t('campaigns:registration.v2.loadSelected')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LoadCampaignModal;
